import type {
  JobEditCompensationPageProps,
  JobEditCompensationValues,
} from './JobEditCompensationPage.component';

// Catálogo de "atalho" exibido como chips na seção Remuneração. Os nomes
// SÃO IGUAIS aos do seed `core.benefit` (core/backend/seeds/reference-tables/
// benefits.json) — Title Case, sem hífens, sem parênteses. Quando o programa
// vinculado à vaga tem um benefício do seed via FK, o Core retorna o
// `benefit.name` original, então `Set`-dedupe casa naturalmente e não
// renderiza dois chips para o mesmo benefício (locked + unlocked).
// Defesa adicional via `normalizeBenefitKey` em JobEditCompensationPage.tsx
// cobre `custom_name` digitados com variações de caixa/acento.
export const BENEFIT_OPTIONS = [
  'Assistência Médica',
  'Assistência Odontológica',
  'Auxílio Academia',
  'Auxílio Creche',
  'Auxílio Home Office',
  'Cesta Básica',
  'Day Off Aniversário',
  'Seguro de Vida',
  'Vale Alimentação',
  'Vale Refeição',
  'Vale Transporte',
] as const;

// Mesma regra de paridade com o seed (`additional_payment.json`). O seed
// hoje tem só 4 entradas; mantemos um catálogo um pouco mais amplo aqui
// (os extras viram `custom_name` ao salvar). Os 4 que SIM existem no seed
// usam exatamente a string do seed pra dedupar.
export const ADDITIONAL_PAYMENT_OPTIONS = [
  'Adc. insalubridade',
  'Adc. periculosidade',
  'Bônus',
  'Comissão',
  'Adicional de sobreaviso',
  'Adicional noturno',
  'Bônus por resultado',
  'Décimo terceiro',
  'Horas extras',
  'PLR',
  'Premiação por metas',
] as const;

// JRN-046 — fixture representando um programa hipotético com esses
// benefícios/pagamentos. Não é mais usado pela página em runtime — o locked
// dinâmico vem de `job.programBenefitNames` /
// `job.programAdditionalPaymentNames` no GET. Mantido como fixture para
// Storybook e tests. Mesmas strings do seed Core para que tests de dedupe
// reflitam o cenário real.
export const LOCKED_BENEFITS = [
  'Day Off Aniversário',
  'Seguro de Vida',
  'Vale Alimentação',
] as const;

export const LOCKED_ADDITIONAL_PAYMENTS = [] as const;

export const emptyValues: JobEditCompensationValues = {
  salaryDisplay: '',
  salary: '',
  salaryMin: '',
  salaryMax: '',
  selectedBenefits: [...LOCKED_BENEFITS],
  selectedAdditionalPayments: [...LOCKED_ADDITIONAL_PAYMENTS],
};

export const filledValues: JobEditCompensationValues = {
  salaryDisplay: 'valor-exato',
  salary: '5.000,00',
  salaryMin: '',
  salaryMax: '',
  selectedBenefits: [...LOCKED_BENEFITS, 'Assistência Médica', 'Auxílio Home Office'],
  selectedAdditionalPayments: ['Décimo terceiro', 'PLR'],
};

export const rangeValues: JobEditCompensationValues = {
  ...filledValues,
  salaryDisplay: 'faixa-salarial',
  salary: '',
  salaryMin: '3.000,00',
  salaryMax: '5.000,00',
};

const baseProps: Omit<JobEditCompensationPageProps, 'values'> = {
  jobName: 'Desenvolvimento de softwares',
  errors: {},
  isSubmitting: false,
  canSubmit: true,
  sectionOptional: false,
  hasSomethingToSave: true,
  benefitOptions: BENEFIT_OPTIONS,
  lockedBenefits: LOCKED_BENEFITS,
  additionalPaymentOptions: ADDITIONAL_PAYMENT_OPTIONS,
  lockedAdditionalPayments: LOCKED_ADDITIONAL_PAYMENTS,
  onFieldChange: () => undefined,
  onToggleBenefit: () => undefined,
  onToggleAdditionalPayment: () => undefined,
  onAddBenefit: () => undefined,
  onAddAdditionalPayment: () => undefined,
  onBack: () => undefined,
  onSubmit: () => undefined,
};

export const emptyProps: JobEditCompensationPageProps = {
  ...baseProps,
  values: emptyValues,
};

export const filledProps: JobEditCompensationPageProps = {
  ...baseProps,
  values: filledValues,
};

export const rangeProps: JobEditCompensationPageProps = {
  ...baseProps,
  values: rangeValues,
};
